import { useId, useMemo, useRef, useState } from 'react';
import type { IndexModelOption } from '@/lib/openrouter-models';

/** Rows rendered at once; the rest are reached by typing. */
const MAX_SHOWN = 50;

function price(m: IndexModelOption): string {
  if (m.free) return 'free · rate-limited';
  if (m.promptPerMTok === null) return 'priced per request';
  const fmt = (n: number | null) => (n === null ? '?' : `$${n < 1 ? n.toFixed(3) : n.toFixed(2)}`);
  return `${fmt(m.promptPerMTok)} in / ${fmt(m.completionPerMTok)} out per 1M tokens`;
}

function context(m: IndexModelOption): string | null {
  if (!m.contextLength) return null;
  return m.contextLength >= 1_000_000
    ? `${(m.contextLength / 1_000_000).toFixed(m.contextLength % 1_000_000 ? 1 : 0)}M ctx`
    : `${Math.round(m.contextLength / 1000)}K ctx`;
}

/**
 * Searchable OpenRouter model picker for the CI index model. Empty `value`
 * means the instance default (`recommended`). Falls back to a plain text field
 * when the list could not be loaded, so an OpenRouter outage never blocks
 * editing the rest of the CI config.
 */
export function ModelPicker({
  value,
  onChange,
  models,
  recommended,
  loadError,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  models: IndexModelOption[] | null;
  recommended: string | null;
  loadError: string | null;
  disabled?: boolean;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const byId = useMemo(() => new Map((models ?? []).map((m) => [m.id, m])), [models]);
  const recommendedModel = recommended ? byId.get(recommended) : undefined;

  // The instance default first, then matches with the recommended model pinned.
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (models ?? []).filter(
      (m) => !q || m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)
    );
    const pinned = recommendedModel && matches.includes(recommendedModel) ? [recommendedModel] : [];
    const rest = matches.filter((m) => m !== recommendedModel);
    return { shown: [...pinned, ...rest].slice(0, MAX_SHOWN), total: matches.length };
  }, [models, query, recommendedModel]);
  const showDefault = !query.trim();
  // Row 0 is "instance default" when it is shown.
  const rowCount = (showDefault ? 1 : 0) + options.shown.length;

  if (!models) {
    return (
      <div className="flex flex-col gap-1">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. google/gemini-2.5-flash-lite — blank uses the instance default"
          className="lc-input py-1.5 font-mono text-xs"
          autoComplete="off"
          spellCheck={false}
        />
        {loadError && (
          <span className="text-2xs text-warn">
            Model list unavailable ({loadError}); enter an OpenRouter model id.
          </span>
        )}
      </div>
    );
  }

  const selected = value ? byId.get(value) : recommendedModel;

  function choose(row: number) {
    if (showDefault && row === 0) onChange('');
    else onChange(options.shown[row - (showDefault ? 1 : 0)].id);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? Math.min(i + 1, rowCount - 1) : Math.max(i - 1, 0);
        listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    } else if (e.key === 'Enter' && open && rowCount > 0) {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div className="relative flex flex-col gap-1">
      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && rowCount > 0 ? `${listId}-${active}` : undefined}
        value={open ? query : value}
        placeholder={recommended ? `Instance default — ${recommended}` : 'Search OpenRouter models'}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setActive(0);
        }}
        // Deferred so a click on an option lands before the list closes.
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        className="lc-input py-1.5 font-mono text-xs"
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          className="absolute top-full z-20 mt-1 max-h-72 w-full overflow-y-auto border border-rule bg-panel shadow-lg"
        >
          {showDefault && (
            <li
              id={`${listId}-0`}
              role="option"
              aria-selected={value === ''}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(0)}
              className={`cursor-pointer px-3 py-2 text-xs ${active === 0 ? 'bg-bg' : ''}`}
            >
              <span className="font-medium text-ink">Instance default</span>
              <span className="block text-2xs text-dim">
                {recommended ?? 'the server’s index model'} — follows the instance if it changes
              </span>
            </li>
          )}
          {options.shown.map((m, i) => {
            const row = i + (showDefault ? 1 : 0);
            return (
              <li
                key={m.id}
                id={`${listId}-${row}`}
                role="option"
                aria-selected={m.id === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(row)}
                className={`cursor-pointer px-3 py-2 text-xs ${active === row ? 'bg-bg' : ''}`}
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-ink">
                    {m.name}
                    {m.id === recommended && (
                      <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-3xs font-medium uppercase text-accent">
                        recommended
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-2xs text-dim">{context(m)}</span>
                </span>
                <span className="block font-mono text-2xs text-dim">{m.id}</span>
                <span className="block text-2xs text-dim">{price(m)}</span>
              </li>
            );
          })}
          {options.total === 0 && (
            <li className="px-3 py-2 text-xs text-dim">No model matches “{query}”.</li>
          )}
          {options.total > options.shown.length && (
            <li className="px-3 py-2 text-2xs text-dim">
              {options.total - options.shown.length} more — keep typing to narrow down.
            </li>
          )}
        </ul>
      )}
      <span className="text-2xs text-dim">
        {selected ? (
          <>
            {value ? '' : 'Instance default: '}
            {selected.name} · {price(selected)}
          </>
        ) : value ? (
          <span className="text-warn">
            “{value}” is not in OpenRouter’s list of models that can index.
          </span>
        ) : (
          'Uses the instance’s index model.'
        )}{' '}
        Only models that support JSON output are listed — indexing requires it.
      </span>
    </div>
  );
}
